import { arrayOf, number, object, shape, string } from 'prop-types';
import React from 'react';
import { hasFileExtension, imageUrl } from 'util/images';
import PictureSource from 'components/PictureSource';

export const CROP_RENDER_OPS = { resizeMode: 'crop' };

/**
 * handles rendering a full <picture> element with fallback image.
 *
 */
function Picture(props) {
  const { className, url, alt, sourceSets, fallbackDims, imgRenderOps } = props;

  const convertToVideo = hasFileExtension(url, 'gif');

  if (convertToVideo) {
    // Making an assumption that the largest widths are used
    // in the first object of the sourceSets array. Per the <picture>
    // spec, the order of sources in a <picture> matters, so
    // in a properly formatted set of sources, we can expect they
    // widest media queries to come first. Generally, the larger the
    // viewport, the larger the size of the Picture.
    //
    // Use the img fallback dimensions if sourceSets is missing
    const largestSize =
      sourceSets.length > 0 && sourceSets[0].layoutWidth
        ? sourceSets[0].layoutWidth
        : fallbackDims.width;

    const videoWidth =
      largestSize.units === 'px'
        ? // if the units are pixels, we just use the value
          largestSize.value
        : // if the units are not pixels, we assume they are vw
          // so we use the units as a percentage of 1024
          // which is when we stop using a fluid responsive layout.
          (1024 * largestSize.units) / 100;

    // Change format to "mp4" if the image is a gif
    const videoRenderOps = Object.assign(
      {},
      imgRenderOps,
      convertToVideo && {
        format: 'mp4',
        width: videoWidth
      }
    );

    return (
      <video className={className} autoPlay muted playsInline loop>
        <source src={imageUrl(url, videoRenderOps)} type="video/mp4" />
      </video>
    );
  }

  const fallbackImgRenderOps = {
    ...CROP_RENDER_OPS,
    ...imgRenderOps,
    ...fallbackDims
  };

  return (
    <picture className={className}>
      {sourceSets.map(sourceProps => (
        <PictureSource
          key={sourceProps.media}
          url={url}
          imgRenderOps={{ ...CROP_RENDER_OPS, ...imgRenderOps }}
          {...sourceProps}
        />
      ))}
      <img src={imageUrl(url, fallbackImgRenderOps)} alt={alt} />
    </picture>
  );
}

Picture.defaultProps = {
  className: undefined,
  alt: '',
  sourceSets: [],
  imgRenderOps: {}
};

Picture.propTypes = {
  className: string,
  url: string.isRequired,
  alt: string,
  sourceSets: arrayOf(object),
  fallbackDims: shape({
    width: number,
    height: number
  }).isRequired,
  imgRenderOps: shape({})
};

export default Picture;
